import { useEffect, useState } from 'react';

import { getDevicesStatusSummary, sortByStatus } from './utils';

import type { MultiSelectVehicleAssociationType } from '@cobliteam/common-types';

import { DeviceGeneralStatus } from '@cobliteam/api-lib/src/__generated__/graphql';

import { filterDeviceByAssociationStatus } from '@cobliteam/devices/src/containers/DataTableDevices/utils';

import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';

import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import DeviceStatusItem from './components/DeviceStatusItem';
import DeviceStatusTitle from './components/DeviceStatusTitle';

interface MultiSelectDeviceStatusProps {
  size?: 'condensed' | 'regular';
  selectedStatus: DeviceGeneralStatus[];
  associationStatus: MultiSelectVehicleAssociationType;
  isOpen?: boolean;
  titleComponent?: React.ReactNode;
  onClick?: () => void;
  onChange: (status: DeviceGeneralStatus[]) => void;
}

const MultiSelectDeviceStatus = ({
  size = 'regular',
  selectedStatus,
  associationStatus,
  isOpen,
  titleComponent,
  onClick: onDropdownClick,
  onChange,
}: MultiSelectDeviceStatusProps) => {
  const { data } = useGetDevices({ options: { enabled: false } });
  const devices = data?.filter((device) => filterDeviceByAssociationStatus(device, associationStatus));

  const { NORMAL, NO_SIGNAL, UNAVAILABLE, DISCONNECTED, NO_SUFFICIENT_DATA, LOSING_DATA } = getDevicesStatusSummary(devices || []);

  const statusSummary = {
    [DeviceGeneralStatus.Normal]: NORMAL,
    [DeviceGeneralStatus.Syncing]: null,
    [DeviceGeneralStatus.NoSignal]: NO_SIGNAL,
    [DeviceGeneralStatus.Unavailable]: UNAVAILABLE,
    [DeviceGeneralStatus.Disconnected]: DISCONNECTED,
    [DeviceGeneralStatus.NoSufficientData]: NO_SUFFICIENT_DATA,
    [DeviceGeneralStatus.LosingData]: LOSING_DATA,
    [DeviceGeneralStatus.Damaged]: null,
  };

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<DeviceGeneralStatus[]>(selectedStatus || []);

  const hasSameElements =
    selectedStatus?.length === draft?.length &&
    Array.from(selectedStatus).every((value) => {
      return draft.find((item) => item === value);
    });

  const onClick = () => {
    onDropdownClick?.();
    setDraft(selectedStatus || []);
  };

  const handleClose = () => {
    setForceClosed(true);
    setDraft(selectedStatus || []);
  };

  const handleApply = () => {
    onChange(draft);
    handleClose();
  };

  const toggleItem = (dataItem: DeviceGeneralStatus) => {
    const isSelected = draft.find((d) => d === dataItem);
    const updatedSelected = isSelected ? draft.filter((d) => d !== dataItem) : [...draft, dataItem];
    setDraft(updatedSelected);
  };

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const dataFiltered = Object.values(DeviceGeneralStatus)
    .reverse()
    .filter((status) => status !== DeviceGeneralStatus.NoSufficientData && status !== DeviceGeneralStatus.LosingData)
    .filter((item) => statusSummary[item] !== null)
    .sort(sortByStatus);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='condensed' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='ml-5'>
            <Dropdown.List
              heightList={300}
              heightItem={40}
              data={dataFiltered}
              itemComponent={(item: DeviceGeneralStatus) => (
                <DeviceStatusItem status={item} count={statusSummary[item]} onChange={() => toggleItem(item)} checked={draft.some((d) => d === item)} />
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='text' full size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='condensed' full onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      {titleComponent || <DeviceStatusTitle size={size} selectedStatus={selectedStatus} />}
    </Dropdown>
  );
};

export default MultiSelectDeviceStatus;
